/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useVisiblePolling } from "../lib/useVisiblePolling";
import { api } from "../lib/apiClient";
import { CheckCircle2, Clock, Radio, Trash2, Pencil, Save, X } from "lucide-react";
import { fmtTimeIST, fmtLiveClockIST } from "../lib/formatDateTime";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import PeopleTagger, { PeoplePills } from "../components/PeopleTagger";

const fmtTime = fmtTimeIST;

export default function RunSheetPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // full row object being edited
  const [savingEdit, setSavingEdit] = useState(false);
  // UI-only toggle: which picker to show inside the single "Responsibility"
  // panel. Data model stays unchanged — both tagged_people and tagged_vendors
  // remain on the payload — switching only hides/shows the picker.
  const [respMode, setRespMode] = useState("person");
  const [editRespMode, setEditRespMode] = useState("person");
  const [preMatchDepts, setPreMatchDepts] = useState([]);
  const [vendorDirectory, setVendorDirectory] = useState([]);
  const [form, setForm] = useState({
    scheduled_time: new Date(Date.now() + 3600 * 1000).toISOString().slice(0, 16),
    trigger: "TIME",
    activity: "",
    lead_department: "",
    area: "",
    instruction: "",
    tagged_people: [],
    tagged_vendors: [],
  });

  const load = async () => {
    if (!DEMO) return;
    const [rs, deps, vends] = await Promise.all([
      api.get(`/matches/${DEMO}/runsheet`),
      api.get(`/reference/pre-match-departments?match_id=${DEMO}`),
      api.get(`/matches/${DEMO}/vendors`),
    ]);
    setRows(rs.data);
    const sorted = [...(deps.data.departments || [])].sort((a, b) => a.localeCompare(b));
    setPreMatchDepts(sorted);
    setVendorDirectory(vends.data || []);
    setForm((f) => ({ ...f, lead_department: f.lead_department || sorted[0] || "" }));
  };
  useVisiblePolling(load, 20000, [DEMO]);
  useEffect(() => {
    // Second-hand ticker for the "T-nn:nn" countdown badges; pauses when
    // the tab is hidden to avoid needless renders.
    let c = null;
    const start = () => { c = setInterval(() => setNow(Date.now()), 1000); };
    const stop = () => { if (c) { clearInterval(c); c = null; } };
    const onVis = () => (document.hidden ? stop() : start());
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); stop(); };
  }, []);

  const act = async (rowId, action) => {
    await api.post(`/runsheet/${rowId}/action`, { action });
    load();
  };

  const remove = async (rowId) => {
    if (!window.confirm("Delete this run sheet item?")) return;
    await api.delete(`/runsheet/${rowId}`);
    load();
  };

  const beginEdit = (row) => {
    // Convert ISO scheduled_time back to the local <input type=datetime-local> shape.
    const localSched = row.scheduled_time
      ? new Date(row.scheduled_time).toISOString().slice(0, 16)
      : "";
    setEditing({
      ...row,
      scheduled_time: localSched,
      tagged_people: row.tagged_people || [],
      tagged_vendors: row.tagged_vendors || [],
    });
    // Default the Responsibility toggle to whichever side already has data,
    // so operators don't lose sight of existing assignments on open.
    const hasVendors = (row.tagged_vendors || []).length > 0;
    const hasPeople = (row.tagged_people || []).length > 0;
    setEditRespMode(hasVendors && !hasPeople ? "vendor" : "person");
  };
  const cancelEdit = () => setEditing(null);
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSavingEdit(true);
    try {
      const payload = {
        scheduled_time: new Date(editing.scheduled_time).toISOString(),
        trigger: editing.trigger,
        activity: editing.activity,
        lead_department: editing.lead_department,
        area: editing.area,
        instruction: editing.instruction,
        responsible_officer: editing.responsible_officer || "",
        tagged_people: editing.tagged_people || [],
        tagged_vendors: editing.tagged_vendors || [],
      };
      await api.patch(`/runsheet/${editing.id}`, payload);
      setEditing(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not save changes.");
    } finally {
      setSavingEdit(false);
    }
  };
  const toggleEditVendor = (v) => {
    setEditing((row) => {
      if (!row) return row;
      const has = (row.tagged_vendors || []).some((x) => x.id === v.id);
      const next = has
        ? row.tagged_vendors.filter((x) => x.id !== v.id)
        : [...(row.tagged_vendors || []), { id: v.id, name: v.name, email: v.email, departments: v.vendor_departments }];
      return { ...row, tagged_vendors: next };
    });
  };

  const create = async (e) => {
    e.preventDefault();
    const payload = { ...form, scheduled_time: new Date(form.scheduled_time).toISOString() };
    await api.post(`/matches/${DEMO}/runsheet`, payload);
    setForm({ ...form, activity: "", instruction: "", tagged_people: [], tagged_vendors: [] });
    setShowNew(false);
    load();
  };

  const toggleVendor = (v) => {
    setForm((f) => {
      const has = f.tagged_vendors.some((x) => x.id === v.id);
      const next = has
        ? f.tagged_vendors.filter((x) => x.id !== v.id)
        : [...f.tagged_vendors, { id: v.id, name: v.name, email: v.email, departments: v.vendor_departments }];
      return { ...f, tagged_vendors: next };
    });
  };

  if (!selectedMatchId) return <RequireMatch />;
  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-runsheet">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Live Command · Match Day</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Run Sheet</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="font-mono-live text-sm text-zinc-400 flex items-center gap-2">
            <Radio className="w-4 h-4 text-readyGreen animate-pulse" /> LIVE · {fmtLiveClockIST(now)} <span className="text-[10px] text-zinc-500 ml-1">IST</span>
          </div>
          <button
            onClick={() => setShowNew((v) => !v)}
            className="bg-readyGreen text-white px-4 py-2 font-bold uppercase tracking-widest text-xs hover:brightness-110"
            data-testid="runsheet-new-btn"
          >
            {showNew ? "Cancel" : "+ Add Item"}
          </button>
        </div>
      </div>

      {showNew && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="runsheet-form">
          <input type="datetime-local" className="bg-black border border-cmdBorder p-2 text-sm" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} required />
          <input className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2" placeholder="Activity" required value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} data-testid="runsheet-form-activity" />
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.lead_department}
            onChange={(e) => setForm({ ...form, lead_department: e.target.value })}
            data-testid="runsheet-form-lead-dept"
            required
          >
            {preMatchDepts.length === 0 && <option value="">Loading…</option>}
            {preMatchDepts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <input className="bg-black border border-cmdBorder p-2 text-sm" placeholder="Area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
          <input className="bg-black border border-cmdBorder p-2 text-sm md:col-span-3" placeholder="Instruction" value={form.instruction} onChange={(e) => setForm({ ...form, instruction: e.target.value })} />
          <div className="md:col-span-4 border border-cmdBorder p-3" data-testid="runsheet-responsibility">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <label className="mc-label">Responsibility</label>
              <div className="flex items-center gap-1" data-testid="runsheet-resp-switch">
                <button
                  type="button"
                  onClick={() => setRespMode("person")}
                  className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 border transition-colors ${
                    respMode === "person"
                      ? "border-readyGreen bg-readyGreen text-white"
                      : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
                  }`}
                  data-testid="runsheet-resp-mode-person"
                >
                  Person{form.tagged_people.length > 0 ? ` · ${form.tagged_people.length}` : ""}
                </button>
                <button
                  type="button"
                  onClick={() => setRespMode("vendor")}
                  className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 border transition-colors ${
                    respMode === "vendor"
                      ? "border-readyGreen bg-readyGreen text-white"
                      : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
                  }`}
                  data-testid="runsheet-resp-mode-vendor"
                >
                  Vendor{form.tagged_vendors.length > 0 ? ` · ${form.tagged_vendors.length}` : ""}
                </button>
              </div>
            </div>
            {respMode === "person" ? (
              <PeopleTagger
                value={form.tagged_people}
                onChange={(v) => setForm({ ...form, tagged_people: v })}
                placeholder="Tag responsible officer(s)…"
                testidPrefix="runsheet-tagger"
              />
            ) : (
              <div>
                <p className="text-[11px] text-zinc-500 mb-2">Pick from vendor accounts in User Management assigned to this match.</p>
                <div className="flex flex-wrap gap-1.5" data-testid="runsheet-vendor-picker">
                  {vendorDirectory.length === 0 && (
                    <span className="text-[11px] text-zinc-500 italic">No vendors assigned to this match yet.</span>
                  )}
                  {vendorDirectory.map((v) => {
                    const picked = form.tagged_vendors.some((x) => x.id === v.id);
                    return (
                      <button
                        type="button"
                        key={v.id}
                        onClick={() => toggleVendor(v)}
                        data-testid={`runsheet-vendor-chip-${v.email}`}
                        className={`text-[11px] px-2 py-1 border transition-colors ${
                          picked
                            ? "border-readyGreen bg-readyGreen/10 text-readyGreen"
                            : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
                        }`}
                        title={`${v.name} · ${(v.vendor_departments || []).join(", ") || "—"}`}
                      >
                        {v.name}
                        {v.vendor_departments?.length > 0 && (
                          <span className="ml-1 text-[9px] text-zinc-500">· {v.vendor_departments.join(", ")}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <button type="submit" className="bg-readyGreen text-white font-bold uppercase tracking-widest text-xs py-2 md:col-span-1" data-testid="runsheet-form-submit">+ Add Item</button>
        </form>
      )}

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          data-testid="rs-edit-modal"
          onClick={cancelEdit}
        >
          <form
            onSubmit={saveEdit}
            onClick={(e) => e.stopPropagation()}
            className="mc-panel w-full max-w-3xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 max-h-[90vh] overflow-y-auto"
          >
            <div className="md:col-span-4 flex items-center justify-between border-b border-cmdBorder pb-3">
              <div>
                <div className="mc-label">Edit Run Sheet Item</div>
                <div className="text-xs text-zinc-500 mt-1">Changes overwrite the row — status & action timestamps stay put.</div>
              </div>
              <button type="button" onClick={cancelEdit} className="border border-cmdBorder p-1.5 hover:border-white" data-testid="rs-edit-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              type="datetime-local"
              className="bg-black border border-cmdBorder p-2 text-sm"
              value={editing.scheduled_time}
              onChange={(e) => setEditing({ ...editing, scheduled_time: e.target.value })}
              required
              data-testid="rs-edit-scheduled"
            />
            <input
              className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2"
              placeholder="Activity"
              required
              value={editing.activity || ""}
              onChange={(e) => setEditing({ ...editing, activity: e.target.value })}
              data-testid="rs-edit-activity"
            />
            <select
              className="bg-black border border-cmdBorder p-2 text-sm"
              value={editing.lead_department || ""}
              onChange={(e) => setEditing({ ...editing, lead_department: e.target.value })}
              data-testid="rs-edit-lead-dept"
              required
            >
              {preMatchDepts.length === 0 && <option value="">Loading…</option>}
              {preMatchDepts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <input
              className="bg-black border border-cmdBorder p-2 text-sm"
              placeholder="Area"
              value={editing.area || ""}
              onChange={(e) => setEditing({ ...editing, area: e.target.value })}
              data-testid="rs-edit-area"
            />
            <input
              className="bg-black border border-cmdBorder p-2 text-sm md:col-span-3"
              placeholder="Instruction"
              value={editing.instruction || ""}
              onChange={(e) => setEditing({ ...editing, instruction: e.target.value })}
              data-testid="rs-edit-instruction"
            />
            <div className="md:col-span-4 border border-cmdBorder p-3" data-testid="rs-edit-responsibility">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <label className="mc-label">Responsibility</label>
                <div className="flex items-center gap-1" data-testid="rs-edit-resp-switch">
                  <button
                    type="button"
                    onClick={() => setEditRespMode("person")}
                    className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 border transition-colors ${
                      editRespMode === "person"
                        ? "border-readyGreen bg-readyGreen text-white"
                        : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
                    }`}
                    data-testid="rs-edit-resp-mode-person"
                  >
                    Person{(editing.tagged_people || []).length > 0 ? ` · ${editing.tagged_people.length}` : ""}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditRespMode("vendor")}
                    className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 border transition-colors ${
                      editRespMode === "vendor"
                        ? "border-readyGreen bg-readyGreen text-white"
                        : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
                    }`}
                    data-testid="rs-edit-resp-mode-vendor"
                  >
                    Vendor{(editing.tagged_vendors || []).length > 0 ? ` · ${editing.tagged_vendors.length}` : ""}
                  </button>
                </div>
              </div>
              {editRespMode === "person" ? (
                <PeopleTagger
                  match_id={DEMO}
                  value={editing.tagged_people || []}
                  onChange={(next) => setEditing({ ...editing, tagged_people: next })}
                  testidPrefix="rs-edit-tagger"
                />
              ) : (
                <div>
                  <p className="text-[11px] text-zinc-500 mb-2">Pick from vendor accounts assigned to this match.</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="rs-edit-vendor-picker">
                    {vendorDirectory.length === 0 && (
                      <span className="text-[11px] text-zinc-500 italic">No vendors assigned to this match yet.</span>
                    )}
                    {vendorDirectory.map((v) => {
                      const selected = (editing.tagged_vendors || []).some((x) => x.id === v.id);
                      return (
                        <button
                          type="button"
                          key={v.id}
                          onClick={() => toggleEditVendor(v)}
                          className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border transition-colors ${
                            selected ? "border-readyGreen bg-readyGreen text-white" : "border-cmdBorder text-zinc-400 hover:border-readyGreen hover:text-readyGreen"
                          }`}
                          data-testid={`rs-edit-vendor-chip-${v.email}`}
                        >
                          {v.name} · {(v.vendor_departments || []).slice(0, 2).join(", ") || "—"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="md:col-span-4 flex items-center justify-end gap-2 pt-2 border-t border-cmdBorder">
              <button
                type="button"
                onClick={cancelEdit}
                className="border border-cmdBorder px-4 py-2 text-xs uppercase tracking-widest hover:border-white"
                data-testid="rs-edit-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingEdit}
                className="flex items-center gap-1 bg-readyGreen text-white font-bold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60"
                data-testid="rs-edit-save"
              >
                <Save className="w-3 h-3" /> {savingEdit ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="border-l-2 border-cmdBorder pl-6 space-y-4">
        {rows.map((r) => {
          const scheduled = new Date(r.scheduled_time).getTime();
          const past = now > scheduled;
          const completed = r.status === "completed";
          const isLate = past && !completed;
          const dotColor = completed ? "#16A34A" : isLate ? "#D97706" : "#94A3B8";
          return (
            <div
              key={r.id}
              className={`mc-panel p-4 relative ${isLate ? "animate-blink-row" : ""}`}
              data-testid={`runsheet-row-${r.id}`}
            >
              <span
                className="absolute w-3 h-3 rounded-full -left-[31px] top-6 ring-4 ring-obsidian"
                style={{ backgroundColor: dotColor }}
              />
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-6 md:col-span-2">
                  <div className="mc-label">Scheduled</div>
                  <div className="font-mono-live text-2xl font-bold mt-1">{fmtTime(r.scheduled_time)}</div>
                </div>
                <div className="col-span-6 md:col-span-5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest border border-cmdBorder px-1.5 py-0.5">{r.trigger}</span>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${
                        completed ? "bg-readyGreen text-white" : isLate ? "border border-warnAmber text-warnAmber animate-pulse" : "border border-cmdBorder text-zinc-400"
                      }`}
                      data-testid={`rs-status-${r.id}`}
                    >
                      {completed ? "Completed" : "Pending"}
                    </span>
                  </div>
                  <div className="font-head font-bold text-xl uppercase mt-2 leading-tight">{r.activity}</div>
                  <div className="text-xs text-zinc-400 mt-1">{r.area} · Lead: <span className="text-white">{r.lead_department}</span></div>
                  {r.instruction && <div className="text-sm text-zinc-300 mt-2 border-l-2 border-cmdBorder pl-2">{r.instruction}</div>}
                  {(r.tagged_people?.length > 0 || r.responsible_officer) && (
                    <div className="text-xs text-zinc-400 mt-2 flex items-center gap-2 flex-wrap">
                      <span className="mc-label">Responsible</span>
                      {r.responsible_officer && <span className="text-white">{r.responsible_officer}</span>}
                      {r.tagged_people?.length > 0 && (
                        <PeoplePills people={r.tagged_people} testidPrefix={`rs-tagged-${r.id}`} />
                      )}
                    </div>
                  )}
                  {r.tagged_vendors?.length > 0 && (
                    <div className="text-xs mt-2 flex items-center gap-2 flex-wrap">
                      <span className="mc-label">Vendors</span>
                      {r.tagged_vendors.map((v) => (
                        <span key={v.id} className="text-[10px] uppercase tracking-widest font-bold border border-readyGreen/50 text-readyGreen px-1.5 py-0.5">
                          {v.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-span-12 md:col-span-5 flex flex-wrap gap-2 items-start justify-end">
                  {!completed ? (
                    <button
                      onClick={() => act(r.id, "complete")}
                      className="flex items-center gap-1 border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-3 py-2 text-xs uppercase tracking-widest transition-colors"
                      data-testid={`rs-complete-${r.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3" /> Mark Completed
                    </button>
                  ) : (
                    <div className="flex flex-col items-end gap-1" data-testid={`rs-done-${r.id}`}>
                      <div className="text-xs text-readyGreen flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> Completed at {r.completed_at ? fmtTime(r.completed_at) : "—"}
                      </div>
                      <button
                        onClick={() => act(r.id, "reopen")}
                        className="text-[10px] uppercase tracking-widest border border-cmdBorder hover:border-warnAmber hover:text-warnAmber px-2 py-1 flex items-center gap-1 transition-colors"
                        data-testid={`rs-reopen-${r.id}`}
                      >
                        <Clock className="w-3 h-3" /> Reopen
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => beginEdit(r)}
                    className="flex items-center gap-1 border border-cmdBorder hover:border-warnAmber hover:text-warnAmber px-2 py-2 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`rs-edit-${r.id}`}
                    title="Edit run sheet item"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    className="flex items-center gap-1 border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-2 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`rs-delete-${r.id}`}
                    title="Delete run sheet item"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
